import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Modal, Field, ModalFooter } from "./Contacts";
import { Buildings, Plus, Trash, MagnifyingGlass, Tag as TagIcon, ArrowsMerge, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { AutoTagModal } from "../components/AutoTagModal";

export default function CompaniesPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [untypedOnly, setUntypedOnly] = useState(false);
  const [busy, setBusy] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [openMerge, setOpenMerge] = useState(false);
  const [openAutoTag, setOpenAutoTag] = useState(false);
  const [pendingReviews, setPendingReviews] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    const d = await api.listCompanies({ q, untyped_only: untypedOnly, page_size: 500 });
    setItems(d.items);
    setTotal(d.total);
  };
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, untypedOnly]);

  const refreshPending = () => api.autotagLatest().then(d => setPendingReviews(d.pending_reviews || 0)).catch(() => {});
  useEffect(() => { refreshPending(); }, []);

  const applyAll = async () => {
    if (!window.confirm("Apply every typed company's tag to all its contacts now?")) return;
    setBusy(true);
    try {
      const r = await api.applyCompanyTags();
      toast.success(`${r.affected_contacts} contact${r.affected_contacts === 1 ? "" : "s"} re-tagged`);
    } finally { setBusy(false); }
  };

  const remove = (c) => setDeleteTarget(c);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-medium tracking-tight">Companies</h2>
          <p className="text-sm text-zinc-500 mt-0.5">
            {total.toLocaleString()} compan{total === 1 ? "y" : "ies"} · kept in sync with your contacts automatically · setting a <strong>Type</strong> automatically tags every contact at that company.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <button data-testid="btn-autotag" onClick={() => setOpenAutoTag(true)} className="relative inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-zinc-200 hover:bg-zinc-50">
            <Sparkle size={14}/> Auto-tag with AI
            {pendingReviews > 0 && <span data-testid="autotag-pending-badge" className="ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">{pendingReviews}</span>}
          </button>
          <button data-testid="btn-merge-suggestions" onClick={() => setOpenMerge(true)} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-zinc-200 hover:bg-zinc-50">
            <ArrowsMerge size={14}/> Suggest merges
          </button>
          <button data-testid="btn-apply-tags" onClick={applyAll} disabled={busy} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-zinc-200 hover:bg-zinc-50 disabled:opacity-50">
            <TagIcon size={14}/> Apply all tags
          </button>
          <button data-testid="btn-new-company" onClick={() => setOpenAdd(true)} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-zinc-950 text-white hover:bg-zinc-800">
            <Plus size={14}/> New company
          </button>
        </div>
      </div>

      <div className="rounded-lg border border-zinc-200 bg-white p-3 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[260px]">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input data-testid="companies-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name…" className="h-8 w-full rounded-md border border-zinc-200 pl-8 pr-3 text-sm focus:outline-none focus:ring-1 focus:ring-zinc-950"/>
        </div>
        <label className="flex items-center gap-2 text-xs text-zinc-600 select-none cursor-pointer">
          <input type="checkbox" checked={untypedOnly} onChange={(e) => setUntypedOnly(e.target.checked)} data-testid="filter-untyped" />
          Show only untyped
        </label>
      </div>

      <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden">
        <div className="max-h-[calc(100vh-340px)] overflow-auto">
          <table className="w-full text-sm">
            <thead className="sticky-thead text-xs text-zinc-500 border-b border-zinc-200">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Company name</th>
                <th className="text-left px-3 py-2 font-medium" style={{ width: "30%" }}>Type (tag)</th>
                <th className="text-right px-3 py-2 font-medium" style={{ width: 120 }}>Contacts</th>
                <th className="text-right px-3 py-2" style={{ width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map(c => (
                <CompanyRow key={c.id} company={c} onChanged={load} onDelete={() => remove(c)} />
              ))}
              {items.length === 0 && (
                <tr><td colSpan={4} className="p-10 text-center text-sm text-zinc-500">
                  {total === 0
                    ? <>No companies yet — they appear here automatically as soon as you have contacts.</>
                    : "No companies match this filter."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {openAdd && <NewCompanyModal onClose={() => setOpenAdd(false)} onSaved={() => { setOpenAdd(false); load(); }} />}
      {openMerge && <MergeSuggestionsModal onClose={() => { setOpenMerge(false); load(); }} />}
      {openAutoTag && <AutoTagModal onClose={() => { setOpenAutoTag(false); load(); refreshPending(); }} onChanged={() => { load(); refreshPending(); }} />}
      {deleteTarget && (
        <DeleteCompanyModal
          company={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDone={() => { setDeleteTarget(null); load(); }}
        />
      )}
    </div>
  );
}

function CompanyRow({ company, onChanged, onDelete }) {
  const [type, setType] = useState(company.company_type || "");
  const [name, setName] = useState(company.name || "");
  const [editingName, setEditingName] = useState(false);
  const [savingType, setSavingType] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => { setType(company.company_type || ""); }, [company.company_type]);
  useEffect(() => { setName(company.name || ""); }, [company.name]);

  const saveType = async (val) => {
    if ((val || "") === (company.company_type || "")) return;
    setSavingType(true);
    try {
      const updated = await api.updateCompany(company.id, { company_type: val });
      if (updated.affected_contacts > 0) {
        toast.success(`Re-tagged ${updated.affected_contacts} contact${updated.affected_contacts === 1 ? "" : "s"} at ${company.name}`);
      }
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSavingType(false); }
  };

  const onTypeChange = (val) => {
    setType(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => saveType(val), 700);
  };

  const saveName = async () => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === company.name) { setEditingName(false); setName(company.name); return; }
    setSavingName(true);
    try {
      const updated = await api.updateCompany(company.id, { name: trimmed });
      if (updated.merged) {
        toast.success(`Merged ${updated.affected_contacts} contact${updated.affected_contacts === 1 ? "" : "s"} from "${updated.merged_from}" into "${updated.name}"`);
      } else {
        toast.success(`Renamed to "${trimmed}" · ${updated.affected_contacts} contact${updated.affected_contacts === 1 ? "" : "s"} updated`);
      }
      setEditingName(false);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rename failed");
      setName(company.name);
    } finally { setSavingName(false); }
  };

  return (
    <tr className="border-b border-zinc-100 hover:bg-zinc-50/60" data-testid={`company-${company.name}`}>
      <td className="px-3 py-2 font-medium text-zinc-900">
        {editingName ? (
          <input
            data-testid={`rename-${company.name}`}
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={saveName}
            onKeyDown={(e) => { if (e.key === "Enter") saveName(); else if (e.key === "Escape") { setEditingName(false); setName(company.name); } }}
            disabled={savingName}
            className="h-7 w-full rounded-md border border-zinc-200 px-2 text-sm focus:outline-none focus:ring-1 focus:ring-zinc-950"
          />
        ) : (
          <button onClick={() => setEditingName(true)} className="text-left hover:underline" data-testid={`edit-name-${company.name}`} title="Click to rename">
            {company.name}
          </button>
        )}
        {company.description && (
          <div className="text-[11px] text-zinc-400 font-normal truncate max-w-[420px]" title={company.description} data-testid={`desc-${company.name}`}>{company.description}</div>
        )}
      </td>
      <td className="px-3 py-2">
        <input
          data-testid={`type-${company.name}`}
          value={type}
          onChange={(e) => onTypeChange(e.target.value)}
          onBlur={(e) => { if (debounceRef.current) clearTimeout(debounceRef.current); saveType(e.target.value); }}
          placeholder="e.g. CRO, Pharma, Biotech…"
          className="h-7 w-full rounded-md border border-zinc-200 px-2 text-sm focus:outline-none focus:ring-1 focus:ring-zinc-950"
        />
        {savingType && <span className="text-[10px] text-zinc-400 ml-1">saving…</span>}
        {(company.capabilities || []).length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1" data-testid={`caps-${company.name}`}>
            {company.capabilities.map(t => (
              <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200">{t.replace("does: ", "")}</span>
            ))}
          </div>
        )}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        {company.contact_count > 0 ? (
          <Link
            to={`/contacts?company=${encodeURIComponent(company.name)}`}
            className="text-zinc-700 hover:text-zinc-950 hover:underline"
            data-testid={`view-contacts-${company.name}`}
            title={`View all ${company.contact_count} contact${company.contact_count === 1 ? "" : "s"} at ${company.name}`}
          >
            {company.contact_count}
          </Link>
        ) : (
          <span className="text-zinc-400">0</span>
        )}
      </td>
      <td className="px-3 py-2 text-right">
        <button onClick={onDelete} className="text-zinc-400 hover:text-rose-700" data-testid={`delete-${company.name}`}><Trash size={14}/></button>
      </td>
    </tr>
  );
}

function NewCompanyModal({ onClose, onSaved }) {
  const [name, setName] = useState("");
  const [type, setType] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.createCompany({ name, company_type: type });
      toast.success("Added");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <Modal title="New company" onClose={onClose}>
      <Field label="Company name"><input data-testid="company-name" value={name} onChange={(e) => setName(e.target.value)} className="modal-input" placeholder="Clarivate"/></Field>
      <div className="mt-3">
        <Field label="Type (becomes a tag on every contact at this company)">
          <input data-testid="company-type" value={type} onChange={(e) => setType(e.target.value)} className="modal-input" placeholder="CRO"/>
        </Field>
      </div>
      <ModalFooter onClose={onClose}>
        <button data-testid="company-save" disabled={busy || !name.trim()} onClick={submit} className="modal-btn-primary">Add</button>
      </ModalFooter>
    </Modal>
  );
}


function DeleteCompanyModal({ company, onClose, onDone }) {
  const [mode, setMode] = useState("keep"); // "keep" or "cascade"
  const [busy, setBusy] = useState(false);
  const n = company.contact_count || 0;
  const confirm = async () => {
    setBusy(true);
    try {
      const r = await api.deleteCompany(company.id, mode === "cascade");
      if (mode === "cascade") {
        toast.success(`Deleted "${company.name}" and ${r.contacts_deleted || 0} contact${(r.contacts_deleted === 1) ? "" : "s"}`);
      } else {
        toast.success(`Deleted "${company.name}" (contacts kept)`);
      }
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <Modal title={`Delete "${company.name}"`} subtitle={n === 0 ? "No contacts are linked to this company." : `${n.toLocaleString()} contact${n === 1 ? " is" : "s are"} linked to this company.`} onClose={onClose}>
      <div className="space-y-2">
        <label data-testid="delete-mode-keep" className={`flex items-start gap-3 rounded-md border p-3 cursor-pointer transition ${mode === "keep" ? "border-zinc-950 bg-zinc-50" : "border-zinc-200 hover:bg-zinc-50"}`}>
          <input type="radio" name="delmode" className="mt-1" checked={mode === "keep"} onChange={() => setMode("keep")} />
          <div className="flex-1">
            <div className="text-sm font-medium text-zinc-900">Delete company only</div>
            <div className="text-xs text-zinc-500 mt-0.5">
              {company.company_type
                ? <>Removes the company entry. The <strong>{company.company_type}</strong> tag will be pulled from its {n} contact{n === 1 ? "" : "s"}, but the contacts themselves are kept.</>
                : <>Removes the company entry only. Its {n} contact{n === 1 ? "" : "s"} are kept untouched.</>}
            </div>
          </div>
        </label>
        <label data-testid="delete-mode-cascade" className={`flex items-start gap-3 rounded-md border p-3 cursor-pointer transition ${mode === "cascade" ? "border-rose-600 bg-rose-50" : "border-zinc-200 hover:bg-zinc-50"}`}>
          <input type="radio" name="delmode" className="mt-1" checked={mode === "cascade"} onChange={() => setMode("cascade")} />
          <div className="flex-1">
            <div className="text-sm font-medium text-rose-700">Delete company AND all {n} contact{n === 1 ? "" : "s"}</div>
            <div className="text-xs text-zinc-500 mt-0.5">Permanently removes every contact at this company, their event history, and their membership in any static list. This cannot be undone.</div>
          </div>
        </label>
      </div>
      <ModalFooter onClose={onClose}>
        <button
          data-testid="delete-confirm"
          disabled={busy}
          onClick={confirm}
          className={`px-3 py-1.5 text-sm font-medium rounded-md text-white ${mode === "cascade" ? "bg-rose-600 hover:bg-rose-700" : "bg-zinc-950 hover:bg-zinc-800"} disabled:opacity-50`}
        >
          {busy ? "Deleting…" : (mode === "cascade" ? `Delete company + ${n} contact${n === 1 ? "" : "s"}` : "Delete company")}
        </button>
      </ModalFooter>
    </Modal>
  );
}

function MergeSuggestionsModal({ onClose }) {
  const [suggestions, setSuggestions] = useState(null);
  const [scanned, setScanned] = useState(0);
  const [busyPair, setBusyPair] = useState(null);

  const load = () => api.mergeSuggestions().then(d => { setSuggestions(d.suggestions); setScanned(d.companies_scanned); });
  useEffect(() => { load(); }, []);

  const merge = async (keep, remove, idx) => {
    if (!window.confirm(`Merge "${remove.name}" into "${keep.name}"?\n\n${remove.contact_count} contact${remove.contact_count === 1 ? "" : "s"} will move to "${keep.name}" and "${remove.name}" will be deleted.`)) return;
    setBusyPair(idx);
    try {
      const r = await api.mergeCompanies(keep.id, remove.id);
      toast.success(`Merged "${r.removed}" into "${r.kept}" · ${r.contacts_moved} contact${r.contacts_moved === 1 ? "" : "s"} moved`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Merge failed");
    } finally { setBusyPair(null); }
  };

  return (
    <Modal title="Suggested merges" subtitle={suggestions === null ? "Scanning company names…" : `Scanned ${scanned} companies — likely duplicates below. The larger company is suggested as the one to keep; use ⇄ to swap.`} onClose={onClose} wide>
      {suggestions === null && <div className="py-10 text-center text-sm text-zinc-400">Comparing every pair of company names…</div>}
      {suggestions?.length === 0 && (
        <div className="py-10 text-center text-sm text-zinc-500" data-testid="merge-none">No likely duplicates found — your company list looks clean.</div>
      )}
      {suggestions?.length > 0 && (
        <div className="space-y-2 max-h-[55vh] overflow-auto pr-1" data-testid="merge-suggestions-list">
          {suggestions.map((s, idx) => <MergePair key={`${s.a.id}-${s.b.id}`} s={s} idx={idx} busy={busyPair === idx} onMerge={merge}/>)}
        </div>
      )}
    </Modal>
  );
}

function MergePair({ s, idx, busy, onMerge }) {
  const [swap, setSwap] = useState(false);
  const keep = swap ? s.b : s.a;
  const remove = swap ? s.a : s.b;
  return (
    <div className="rounded-lg border border-zinc-200 p-3" data-testid={`merge-pair-${idx}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${s.confidence === "high" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>{s.confidence}</span>
        <span className="text-[11px] text-zinc-400">{s.reason}</span>
      </div>
      <div className="mt-2 flex items-center gap-2">
        <div className="flex-1 min-w-0 rounded-md border border-emerald-200 bg-emerald-50/50 px-2.5 py-1.5">
          <div className="text-sm font-medium text-zinc-900 truncate">{keep.name}</div>
          <div className="text-[11px] text-zinc-500">Keep · {keep.contact_count} contact{keep.contact_count === 1 ? "" : "s"}{keep.company_type ? ` · ${keep.company_type}` : ""}</div>
        </div>
        <button data-testid={`merge-swap-${idx}`} onClick={() => setSwap(!swap)} title="Swap which name is kept" className="p-1.5 rounded-md border border-zinc-200 text-zinc-500 hover:bg-zinc-50 shrink-0">⇄</button>
        <div className="flex-1 min-w-0 rounded-md border border-zinc-200 bg-zinc-50 px-2.5 py-1.5">
          <div className="text-sm font-medium text-zinc-500 truncate line-through decoration-zinc-300">{remove.name}</div>
          <div className="text-[11px] text-zinc-400">Merge away · {remove.contact_count} contact{remove.contact_count === 1 ? "" : "s"}{remove.company_type ? ` · ${remove.company_type}` : ""}</div>
        </div>
        <button data-testid={`merge-btn-${idx}`} disabled={busy} onClick={() => onMerge(keep, remove, idx)} className="shrink-0 text-xs font-semibold px-3 py-2 rounded-md bg-zinc-950 text-white hover:bg-zinc-800 disabled:opacity-50">
          {busy ? "Merging…" : "Merge"}
        </button>
      </div>
    </div>
  );
}

